import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Car, Check, X as XIcon } from '@phosphor-icons/react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Instructor-side modal that answers the "is de les gereden?" question for a
 * scheduled lesson whose end-time has passed. `Ja` auto-logs a lesson record
 * (so totals go up). `Nee` marks it as not-driven and does nothing else.
 */
export default function DrivenCheckModal({ open, lessonId, onClose, onLogged }) {
    const [lesson, setLesson] = useState(null);
    const [studentName, setStudentName] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !lessonId) return;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/scheduled-lessons`, { withCredentials: true });
                const l = (data || []).find(x => x.id === lessonId);
                if (l) {
                    setLesson(l);
                    // Try to look up student name
                    try {
                        const { data: students } = await axios.get(`${API}/students`, { withCredentials: true });
                        const s = (students || []).find(x => x.id === l.student_id);
                        if (s) setStudentName(s.name || '');
                    } catch { /* ignore */ }
                }
            } catch { /* ignore */ }
        })();
    }, [open, lessonId]);

    const submit = async (answer) => {
        if (!lessonId || busy) return;
        setBusy(true);
        try {
            const { data } = await axios.post(
                `${API}/scheduled-lessons/${lessonId}/driven`,
                { answer },
                { withCredentials: true }
            );
            if (answer === 'yes') {
                toast.success('Les toegevoegd aan totaal ✅');
                if (onLogged) onLogged(data);
            } else {
                toast.success('Les niet gereden — genoteerd');
            }
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Actie mislukt');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent
                className="bg-[#1A202C] border-2 border-[#D4A537] text-white max-w-md p-0 gap-0 overflow-hidden"
                data-testid="driven-check-modal"
            >
                <div className="px-5 py-4 bg-gradient-to-br from-[#D4A537]/20 via-[#D4A537]/5 to-transparent border-b border-[#4A5568]">
                    <div className="flex items-center gap-2">
                        <Car size={24} weight="fill" className="text-[#D4A537]" />
                        <DialogTitle className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Is de les gereden?
                        </DialogTitle>
                    </div>
                    <DialogDescription className="text-xs text-gray-300 mt-1">
                        Bij Ja wordt de les automatisch bij het totaal opgeteld. Bij Nee gebeurt er niets.
                    </DialogDescription>
                </div>

                <div className="px-5 py-4 space-y-3">
                    <div className="bg-[#2D3748] border border-[#4A5568] rounded-xl p-4 space-y-1.5">
                        <div className="text-[10px] uppercase tracking-widest text-gray-500">Leerling</div>
                        <div className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {studentName || 'Leerling'}
                        </div>
                        {lesson && (
                            <div className="text-sm text-gray-300">
                                Les: <span className="text-white">{lesson.date}</span>
                                {lesson.time && <> om <span className="text-white">{lesson.time}</span></>}
                                {lesson.duration_minutes && <> · <span className="text-white">{lesson.duration_minutes} min</span></>}
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 leading-relaxed">
                        Deze vraag verschijnt automatisch nadat een geplande les afgelopen zou zijn.
                        Zo blijft je totaal-teller kloppend — ook als de leerling de les niet vooraf bevestigde.
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-2 p-3 border-t border-[#4A5568] bg-[#1A202C]">
                    <Button
                        onClick={() => submit('no')}
                        disabled={busy}
                        variant="outline"
                        className="border-red-500/60 bg-red-500/10 text-red-300 hover:bg-red-500/20 hover:text-red-200 font-bold"
                        data-testid="driven-check-no"
                    >
                        <XIcon size={18} weight="bold" className="mr-1" />
                        Nee, niet gereden
                    </Button>
                    <Button
                        onClick={() => submit('yes')}
                        disabled={busy}
                        className="bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
                        data-testid="driven-check-yes"
                    >
                        <Check size={18} weight="bold" className="mr-1" />
                        {busy ? 'Bezig…' : 'Ja, tel mee'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
